<template>
  <RygenQuickSearch
    :results="resolvedResults"
    option-group-label="header"
    option-group-children="items"
    option-label="label"
    @clear="searchResults.data = undefined"
    @search="debounceSearch"
    @select="onSelect"
  >
    <template #result="{ option }: { option: Result }">
      <QuickQuickSearchResponseItem
        v-if="option.searchType === 'QuickQuickSearch'"
        :result="option"
      />
      <RecentlyViewedResultView
        v-else
        :recent="option"
        @domain-branding-refresh="onDomainBrandingRefresh"
      />
    </template>

    <template #loading>
      <QuickSearchLoadingCard />
    </template>
  </RygenQuickSearch>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import { computed, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useRecentlyViewed } from '@/composables';
import {
  QuickQuickSearchResponse,
  QuickQuickSearchResponseItemOrder,
  QuickQuickSearchResponseItemShipment,
  RecentlyViewedResult,
} from '@/model';

import QuickQuickSearchResponseItem from '@/components/search/QuickQuickSearchResponseItem.vue';
import QuickSearchLoadingCard from '@/components/search/QuickSearchLoadingCard.vue';
import RecentlyViewedResultView from '@/components/search/RecentlyViewedResult.vue';
import RygenQuickSearch from '@rygen/rygen-ui/components/ui/QuickSearch.vue';

type Result =
  | RecentlyViewedResult
  | QuickQuickSearchResponseItemOrder
  | QuickQuickSearchResponseItemShipment;
type OptionGroup = {
  header: string;
  items:
    | RecentlyViewedResult[]
    | QuickQuickSearchResponseItemOrder[]
    | QuickQuickSearchResponseItemShipment[];
};

const { t } = useI18n();
const { onDomainBrandingRefresh, recentlyViewed } = useRecentlyViewed();

const cancelSearchToken: Ref<CancelTokenSource | undefined> = ref(undefined);
const searchResults: Ref<
  Loadable<QuickQuickSearchResponse | RecentlyViewedResult[]>
> = ref({
  loading: false,
});
const resolvedResults = computed((): Loadable<OptionGroup[]> => {
  const { loading, data } = searchResults.value;

  if (data === undefined || loading) {
    return { data: [], loading };
  } else if (data instanceof QuickQuickSearchResponse) {
    const orders = data?.orderSearchResponse;
    const shipments = data?.shipmentSearchResponse;

    const networkResults: OptionGroup[] = [];
    if (authorization.hasAuthority('order', 'read') && orders?.length) {
      networkResults.push({ header: t('orders.orders', 2), items: orders });
    }

    if (authorization.hasAuthority('shipment', 'read') && shipments?.length) {
      networkResults.push({
        header: t('shipments.shipments', 2),
        items: shipments,
      });
    }

    return { loading, data: networkResults };
  } else {
    const optionGroup: OptionGroup = {
      header: t('common.recentlyViewed'),
      items: data,
    };
    return {
      loading: false,
      data: [optionGroup],
    };
  }
});

const showRecents = async () => {
  searchResults.value.loading = true;
  // Have to wrap the recents in a promise an await it because PrimeVue's
  // AutoComplete doesn't respond if you just set the data
  searchResults.value.data = await new Promise((resolve) => {
    const recents = recentlyViewed.value.filter((r) => !!r.id);
    resolve(recents);
  });
  searchResults.value.loading = false;
};

const executeSearch = async (searchText: string): Promise<void> => {
  if (cancelSearchToken.value) {
    cancelSearchToken.value.cancel();
  }
  cancelSearchToken.value = axios.CancelToken.source();

  if (!searchText.trim()) {
    return showRecents();
  }

  try {
    searchResults.value.loading = true;
    searchResults.value.data = await api.quickSearch.quickQuickSearch(
      searchText,
      cancelSearchToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(e);
  } finally {
    searchResults.value.loading = false;
  }
};

const debounceSearch = debounce(executeSearch, 500);

const onSelect = (_result: Result) => {
  const contentHeading = document.querySelector(
    '#content-heading',
  ) as HTMLElement | null;

  // AutoComplete sets focus somewhere; wait til its done to set it ourselves
  window.setTimeout(() => contentHeading?.focus(), 500);
};
</script>
